import React from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import Spinner from '@/components/platform/ui/Spinner';

interface DangerButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  variant?: 'outline' | 'solid';
}

export default function DangerButton({
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  size = 'md',
  className = '',
  variant = 'outline',
}: DangerButtonProps) {
  const { darkMode } = useDarkMode();

  const baseClasses =
    variant === 'solid'
      ? 'bg-red-600 font-semibold text-white shadow-xs hover:bg-red-700 inline-flex items-center justify-center gap-2'
      : darkMode
        ? 'bg-red-500/10 font-semibold text-red-400 shadow-xs hover:bg-red-500/20 ring-1 ring-red-500/30 ring-inset inline-flex items-center justify-center gap-2'
        : 'bg-red-500/10 font-semibold text-red-600 ring-1 shadow-xs ring-red-600/30 ring-inset hover:bg-red-500/20 inline-flex items-center justify-center gap-2';

  const sizeClasses = {
    xs: 'rounded-md px-2 py-1.5 text-sm',
    sm: 'rounded-md px-2 py-1.5 text-sm',
    md: 'rounded-md px-3 py-2 text-sm',
    lg: 'rounded-md px-3 py-2 text-sm',
    xl: 'rounded-md px-3.5 py-2.5 text-sm',
  };

  const disabledClasses = 'disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${sizeClasses[size]} ${disabledClasses} ${className}`}
    >
      {loading ? (
        <>
          <Spinner size="sm" darkMode={variant === 'solid'} />
          <span>Loading...</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
